import { computed } from 'vue'

import type { MeasurementUnit, UnitRelation } from '@/domains/measurement-units/types/models/MeasurementUnit.types'
import type { UseEntityQueryOptions } from '@/support/query/UseEntityQueryOptions.types'
import type { SelectOptionGroup } from '@/types/common/models/SelectOption.types'

import { useMeasurementUnitsQuery } from '@/domains/measurement-units/composables/use-measurement-units-query/useMeasurementUnitsQuery'
import {
  baseUnitOfRelation,
  findMeasurementUnitByIdentifier,
  groupMeasurementUnitOptions,
  isPickableUnit,
  lineUnitOptionsFor,
  measurementUnitLabel,
  stockUnitOfRelation,
  stockUnitOptionsFor
} from '@/domains/measurement-units/composables/use-measurement-units-query/useMeasurementUnitsQuery.utils'
import { BASE_UNIT_FACTOR } from '@/domains/measurement-units/types/consts/MeasurementUnitFactor.consts'

/**
 * Composable entry point for measurement units.
 *
 * Wraps the TanStack query composable and provides a stable API
 * with computed options and helper lookups.
 *
 * @example
 * ```typescript
 * const { asGroupedOptions, isLoading, getByIdentifier } = useMeasurementUnits()
 *
 * // Use in template — the group props are what render the headers
 * <InputSelectGroup
 *   :options="asGroupedOptions"
 *   :loading="isLoading"
 *   option-group-children="items"
 *   option-group-label="label"
 * />
 * ```
 */
export const useMeasurementUnits = (options: UseEntityQueryOptions = {}) => {
  const { data, isLoading, isFetched, error } = useMeasurementUnitsQuery(options)

  const units = computed(() => data.value ?? [])
  const isLoaded = computed(() => isFetched.value)
  const queryError = computed(() => (error.value ? String(error.value) : null))
  // Converting pickers offer only activated units with a factor. getByIdentifier stays
  // over the full set, unfiltered, so historical lines still resolve their unit.
  //
  // Grouped rather than flat, and there is no flat variant on purpose: a second
  // accessor is how one picker ends up ungrouped. A caller needing the raw list has
  // `units`.
  const asGroupedOptions = computed(() => groupMeasurementUnitOptions(units.value.filter(isPickableUnit)))

  const getByIdentifier = (unitIdentifier: string) => findMeasurementUnitByIdentifier(units.value, unitIdentifier)

  /** A unit by its row id — what a `unit_id` foreign key holds. Undefined for no id, or one the catalogue lacks. */
  const getById = (id?: number | null): MeasurementUnit | undefined =>
    id === null || id === undefined ? undefined : units.value.find(unit => unit.id === id)

  /**
   * The catalogue's full unit for a relation on the wire — a variant's `pricing_unit`, a line's
   * `measurement_unit`. Those carry no type slug, and `unitUtil` needs one to tell a count from a
   * measure. Undefined until the catalogue loads, so a figure renders bare for that moment.
   */
  const resolve = (unit?: UnitRelation): MeasurementUnit | undefined =>
    unit?.unit_identifier ? getByIdentifier(unit.unit_identifier) : undefined

  /** The base unit a stock figure reads in, for a variant priced in `unit`. */
  const baseUnitOf = (unit?: UnitRelation): MeasurementUnit | undefined => baseUnitOfRelation(units.value, unit)

  /** The unit a variant's stock reads and is typed in. Undefined until the catalogue loads. */
  const stockUnitOf = (relation?: UnitRelation): MeasurementUnit | undefined =>
    stockUnitOfRelation(units.value, relation)

  /** The units a stock input for this variant may be typed in. */
  const stockUnitOptionsOf = (relation?: UnitRelation): MeasurementUnit[] => stockUnitOptionsFor(units.value, relation)

  /** The grouped units a purchase or bill line for this variant may be entered in. Empty before a variant is picked. */
  const lineUnitOptionsOf = (relation?: UnitRelation): SelectOptionGroup[] =>
    groupMeasurementUnitOptions(lineUnitOptionsFor(units.value, relation))

  /**
   * How far one scan moves a base-count quantity: one stock unit (DEC-2026-096 E4). On ham that is 1 kg, held
   * as 1,000 base units. A variant with no stock unit yet steps one base unit.
   */
  const scanStepOf = (pricingUnit?: UnitRelation): number =>
    stockUnitOf(pricingUnit)?.conversion_factor ?? BASE_UNIT_FACTOR

  /**
   * What a unit reads as beside a quantity — `2 kg`, `6 items`, `1 item`.
   *
   * ⚠ **Quantity-aware, because the catalogue's symbol is singular.** The count unit is seeded
   * with symbol `item` and identifier `items`, so three screens resolving `symbol || name`
   * printed "Made from 6 item" on every pack row. `measurementUnitLabel` carries the rule and
   * why; this is the accessor that puts the catalogue behind it.
   *
   * The identifier falls through unchanged when nothing resolves, so a unit a merchant has
   * since deleted still reads as something rather than leaving a bare number.
   */
  const labelFor = (unitIdentifier: string, quantity: number) =>
    measurementUnitLabel(getByIdentifier(unitIdentifier), quantity, unitIdentifier)

  return {
    units,
    isLoading,
    isLoaded,
    error: queryError,
    asGroupedOptions,
    getByIdentifier,
    getById,
    resolve,
    baseUnitOf,
    stockUnitOf,
    stockUnitOptionsOf,
    lineUnitOptionsOf,
    scanStepOf,
    labelFor
  }
}
